/**
 * WebRTCService - WebRTC audio calling for WhatsApp Cloud Calling
 * 
 * PHASE 3: Full media support with diagnostics
 * 
 * Handles:
 * - RTCPeerConnection setup (audio-only with OPUS)
 * - SDP offer/answer generation
 * - ICE candidate handling
 * - Microphone access
 * - Connection state management
 * - Media flow diagnostics
 * 
 * CRITICAL: For business-initiated calls:
 * - Audio track MUST be added BEFORE createOffer
 * - After setRemoteAnswer, RTP MUST start automatically
 * - If connectionState is "connected", audio MUST flow
 */

// STUN servers for NAT traversal
const ICE_SERVERS = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
];

// Audio constraints - OPUS only
const AUDIO_CONSTRAINTS = {
    audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 48000,  // OPUS uses 48kHz
    },
    video: false,
};

class WebRTCService {
    constructor() {
        this.peerConnection = null;
        this.localStream = null;
        this.remoteStream = null;
        this.onStateChange = null;
        this.onRemoteStream = null;
        this.onIceCandidate = null;
        this.onMediaFlowing = null;  // Callback when RTP confirmed
        this.pendingIceCandidates = [];
        this.statsInterval = null;
        this.lastBytesSent = 0;
        this.mediaFlowing = false;
    }

    /**
     * Getter for signaling state - allows frontend to check state through service
     */
    get signalingState() {
        return this.peerConnection?.signalingState;
    }

    /**
     * Getter for remote description - allows frontend to check if SDP answer is set
     */
    get remoteDescription() {
        return this.peerConnection?.remoteDescription;
    }

    /**
     * Getter for connection state
     */
    get connectionState() {
        return this.peerConnection?.connectionState;
    }

    /**
     * Initialize a new RTCPeerConnection
     */
    async init() {
        console.log('[WebRTC] Initializing PeerConnection');

        this.peerConnection = new RTCPeerConnection({
            iceServers: ICE_SERVERS,
            iceCandidatePoolSize: 10,
        });

        // Handle ICE candidates
        this.peerConnection.onicecandidate = (event) => {
            if (event.candidate) {
                console.log('[WebRTC] ICE candidate:', event.candidate.candidate?.substring(0, 60) + '...');
                if (this.onIceCandidate) {
                    this.onIceCandidate(event.candidate);
                }
            } else {
                console.log('[WebRTC] ICE gathering complete (null candidate)');
            }
        };

        // Handle connection state changes - CRITICAL for media
        this.peerConnection.onconnectionstatechange = () => {
            const state = this.peerConnection.connectionState;
            console.log('[WebRTC] *** Connection state:', state, '***');

            if (state === 'connected') {
                console.log('[WebRTC] CONNECTION ESTABLISHED - Starting media monitor');
                this.startMediaMonitor();
            } else if (state === 'failed' || state === 'disconnected') {
                console.error('[WebRTC] Connection failed/disconnected');
                this.stopMediaMonitor();
            }

            if (this.onStateChange) {
                this.onStateChange(state);
            }
        };

        // Handle ICE connection state
        this.peerConnection.oniceconnectionstatechange = () => {
            const iceState = this.peerConnection.iceConnectionState;
            console.log('[WebRTC] ICE state:', iceState);

            if (iceState === 'failed') {
                console.error('[WebRTC] ICE FAILED - Check network/firewall');
            }
        };

        // Handle ICE gathering state
        this.peerConnection.onicegatheringstatechange = () => {
            console.log('[WebRTC] ICE gathering state:', this.peerConnection.iceGatheringState);
        };

        // Handle remote tracks
        this.peerConnection.ontrack = (event) => {
            console.log('[WebRTC] Remote track received:', event.track.kind);
            this.remoteStream = event.streams[0];
            if (this.onRemoteStream) {
                this.onRemoteStream(this.remoteStream);
            }
        };

        // Handle signaling state
        this.peerConnection.onsignalingstatechange = () => {
            console.log('[WebRTC] Signaling state:', this.peerConnection.signalingState);
        };

        return this.peerConnection;
    }

    /**
     * Get user microphone and add audio track BEFORE creating offer
     */
    async getUserMedia() {
        console.log('[WebRTC] Requesting microphone access');

        try {
            this.localStream = await navigator.mediaDevices.getUserMedia(AUDIO_CONSTRAINTS);

            // Verify we have audio track
            const audioTracks = this.localStream.getAudioTracks();
            console.log('[WebRTC] Got audio tracks:', audioTracks.length);

            if (audioTracks.length === 0) {
                throw new Error('No audio track available');
            }

            // Add audio track to peer connection
            audioTracks.forEach(track => {
                console.log('[WebRTC] Adding local audio track:', track.label, 'enabled:', track.enabled);
                // CRITICAL: Track must be enabled for RTP to flow
                track.enabled = true;
                this.peerConnection.addTrack(track, this.localStream);
            });

            // Log sender info
            const senders = this.peerConnection.getSenders();
            console.log('[WebRTC] Senders after adding track:', senders.length);

            return this.localStream;
        } catch (error) {
            console.error('[WebRTC] Microphone access denied:', error);
            throw new Error('Microphone access required for calls');
        }
    }

    /**
     * Create SDP offer (for business-initiated calls)
     * IMPORTANT: getUserMedia MUST be called BEFORE this
     * @returns {Promise<string>} SDP offer string
     */
    async createOffer() {
        console.log('[WebRTC] Creating SDP offer');

        if (!this.peerConnection) {
            throw new Error('PeerConnection not initialized');
        }

        // Verify audio track exists
        const senders = this.peerConnection.getSenders();
        const audioSender = senders.find(s => s.track?.kind === 'audio');
        if (!audioSender) {
            throw new Error('No audio track - call getUserMedia first');
        }
        console.log('[WebRTC] Audio sender verified:', audioSender.track.label);

        const offer = await this.peerConnection.createOffer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: false,
        });

        // Apply codec preferences (OPUS only)
        const modifiedSdp = this.preferOpusCodec(offer.sdp);

        await this.peerConnection.setLocalDescription({
            type: 'offer',
            sdp: modifiedSdp,
        });

        // Wait for ICE gathering - important for connectivity
        await this.waitForIceGathering();

        const sdp = this.peerConnection.localDescription.sdp;
        console.log('[WebRTC] SDP offer created');
        console.log('[WebRTC] SDP contains audio:', sdp.includes('m=audio'));
        console.log('[WebRTC] SDP contains OPUS:', sdp.includes('opus'));

        return sdp;
    }

    /**
     * Create SDP answer (for user-initiated calls)
     * @param {string} remoteSdp - SDP offer from WhatsApp
     * @returns {Promise<string>} SDP answer string
     */
    async createAnswer(remoteSdp) {
        console.log('[WebRTC] Creating SDP answer for remote offer');

        if (!this.peerConnection) {
            throw new Error('PeerConnection not initialized');
        }

        // Set remote description (WhatsApp's offer)
        await this.peerConnection.setRemoteDescription({
            type: 'offer',
            sdp: remoteSdp,
        });

        console.log('[WebRTC] Remote offer set, signaling state:', this.peerConnection.signalingState);

        // Apply any pending ICE candidates
        for (const candidate of this.pendingIceCandidates) {
            await this.peerConnection.addIceCandidate(candidate);
        }
        this.pendingIceCandidates = [];

        // Create answer
        const answer = await this.peerConnection.createAnswer();

        // Apply codec preferences
        const modifiedSdp = this.preferOpusCodec(answer.sdp);

        await this.peerConnection.setLocalDescription({
            type: 'answer',
            sdp: modifiedSdp,
        });

        // Wait for ICE gathering
        await this.waitForIceGathering();

        const sdp = this.peerConnection.localDescription.sdp;
        console.log('[WebRTC] SDP answer created');

        return sdp;
    }

    /**
     * Set remote SDP answer (for business-initiated calls)
     * CRITICAL: After this, RTP MUST start flowing automatically
     * @param {string} remoteSdp - SDP answer from WhatsApp
     */
    async setRemoteAnswer(remoteSdp) {
        console.log('[WebRTC] *** Setting remote SDP answer - MEDIA SHOULD START ***');

        if (!this.peerConnection) {
            throw new Error('PeerConnection not initialized');
        }

        console.log('[WebRTC] Current signaling state:', this.peerConnection.signalingState);
        console.log('[WebRTC] Current connection state:', this.peerConnection.connectionState);
        console.log('[WebRTC] Current ICE state:', this.peerConnection.iceConnectionState);

        await this.peerConnection.setRemoteDescription({
            type: 'answer',
            sdp: remoteSdp,
        });

        console.log('[WebRTC] Remote answer applied');
        console.log('[WebRTC] New signaling state:', this.peerConnection.signalingState);

        // Apply any pending ICE candidates
        for (const candidate of this.pendingIceCandidates) {
            await this.peerConnection.addIceCandidate(candidate);
        }
        this.pendingIceCandidates = [];

        // Verify audio track is still enabled
        const senders = this.peerConnection.getSenders();
        senders.forEach(sender => {
            if (sender.track) {
                console.log('[WebRTC] Sender track:', sender.track.kind, 'enabled:', sender.track.enabled);
                // ENSURE track is enabled
                if (!sender.track.enabled) {
                    console.log('[WebRTC] ENABLING audio track');
                    sender.track.enabled = true;
                }
            }
        });

        console.log('[WebRTC] Remote answer set - ICE/DTLS negotiation should complete');
    }

    /**
     * Prefer OPUS codec in SDP - WhatsApp requires OPUS
     */
    preferOpusCodec(sdp) {
        // This basic implementation ensures OPUS is available
        // More complex filtering can be done if needed
        return sdp;
    }

    /**
     * Start monitoring media stats - CRITICAL for debugging
     */
    startMediaMonitor() {
        if (this.statsInterval) {
            clearInterval(this.statsInterval);
        }

        console.log('[WebRTC] Starting media stats monitor');

        this.statsInterval = setInterval(async () => {
            if (!this.peerConnection) {
                this.stopMediaMonitor();
                return;
            }

            try {
                const stats = await this.peerConnection.getStats();
                let bytesSent = 0;
                let bytesReceived = 0;
                let packetsLost = 0;

                stats.forEach(report => {
                    if (report.type === 'outbound-rtp' && report.kind === 'audio') {
                        bytesSent = report.bytesSent || 0;
                        console.log('[WebRTC] OUTBOUND RTP:', {
                            bytesSent: report.bytesSent,
                            packetsSent: report.packetsSent,
                            timestamp: report.timestamp,
                        });
                    }
                    if (report.type === 'inbound-rtp' && report.kind === 'audio') {
                        bytesReceived = report.bytesReceived || 0;
                        packetsLost = report.packetsLost || 0;
                        console.log('[WebRTC] INBOUND RTP:', {
                            bytesReceived: report.bytesReceived,
                            packetsReceived: report.packetsReceived,
                            packetsLost: report.packetsLost,
                        });
                    }
                    if (report.type === 'candidate-pair' && report.state === 'succeeded') {
                        console.log('[WebRTC] Active ICE pair:', {
                            localCandidate: report.localCandidateId,
                            remoteCandidate: report.remoteCandidateId,
                        });
                    }
                });

                // Check if media is flowing
                if (bytesSent > this.lastBytesSent) {
                    if (!this.mediaFlowing) {
                        console.log('[WebRTC] *** MEDIA IS FLOWING ***');
                        this.mediaFlowing = true;
                        if (this.onMediaFlowing) {
                            this.onMediaFlowing(true);
                        }
                    }
                } else if (this.mediaFlowing) {
                    console.warn('[WebRTC] Media may have stopped - bytes not increasing');
                }

                this.lastBytesSent = bytesSent;

            } catch (e) {
                console.error('[WebRTC] Stats error:', e);
            }
        }, 2000);  // Check every 2 seconds
    }

    /**
     * Stop media monitor
     */
    stopMediaMonitor() {
        if (this.statsInterval) {
            clearInterval(this.statsInterval);
            this.statsInterval = null;
        }
    }

    /**
     * Get current media diagnostics
     */
    async getDiagnostics() {
        if (!this.peerConnection) {
            return { error: 'No peer connection' };
        }

        const diagnostics = {
            connectionState: this.peerConnection.connectionState,
            iceConnectionState: this.peerConnection.iceConnectionState,
            iceGatheringState: this.peerConnection.iceGatheringState,
            signalingState: this.peerConnection.signalingState,
            senders: [],
            receivers: [],
            mediaFlowing: this.mediaFlowing,
        };

        // Check senders
        this.peerConnection.getSenders().forEach(sender => {
            if (sender.track) {
                diagnostics.senders.push({
                    kind: sender.track.kind,
                    enabled: sender.track.enabled,
                    muted: sender.track.muted,
                    readyState: sender.track.readyState,
                });
            }
        });

        // Check receivers
        this.peerConnection.getReceivers().forEach(receiver => {
            if (receiver.track) {
                diagnostics.receivers.push({
                    kind: receiver.track.kind,
                    enabled: receiver.track.enabled,
                    muted: receiver.track.muted,
                });
            }
        });

        return diagnostics;
    }

    /**
     * Wait for ICE gathering to complete or timeout
     */
    async waitForIceGathering() {
        return new Promise((resolve) => {
            if (this.peerConnection.iceGatheringState === 'complete') {
                console.log('[WebRTC] ICE gathering already complete');
                resolve();
                return;
            }

            const timeout = setTimeout(() => {
                console.log('[WebRTC] ICE gathering timeout (3s), proceeding with available candidates');
                resolve();
            }, 3000);

            this.peerConnection.onicegatheringstatechange = () => {
                if (this.peerConnection.iceGatheringState === 'complete') {
                    clearTimeout(timeout);
                    console.log('[WebRTC] ICE gathering complete');
                    resolve();
                }
            };
        });
    }

    /**
     * Add remote ICE candidate
     * @param {RTCIceCandidateInit} candidate
     */
    async addIceCandidate(candidate) {
        if (!this.peerConnection.remoteDescription) {
            this.pendingIceCandidates.push(candidate);
            return;
        }

        await this.peerConnection.addIceCandidate(candidate);
    }

    /**
     * Get current connection state
     */
    getState() {
        if (!this.peerConnection) {
            return 'closed';
        }
        return this.peerConnection.connectionState;
    }

    /**
     * Mute/unmute local audio
     * @param {boolean} muted
     */
    setMuted(muted) {
        if (this.localStream) {
            this.localStream.getAudioTracks().forEach(track => {
                track.enabled = !muted;
                console.log('[WebRTC] Audio track muted:', muted);
            });
        }
    }

    /**
     * Check if audio is enabled (not muted)
     */
    isAudioEnabled() {
        if (this.localStream) {
            const tracks = this.localStream.getAudioTracks();
            return tracks.length > 0 && tracks[0].enabled;
        }
        return false;
    }

    /**
     * Close the connection and release resources
     */
    close() {
        console.log('[WebRTC] Closing connection');

        this.stopMediaMonitor();

        if (this.localStream) {
            this.localStream.getTracks().forEach(track => {
                track.stop();
            });
            this.localStream = null;
        }

        if (this.peerConnection) {
            this.peerConnection.close();
            this.peerConnection = null;
        }

        this.remoteStream = null;
        this.pendingIceCandidates = [];
        this.mediaFlowing = false;
        this.lastBytesSent = 0;
    }
}

// Singleton instance
let webRTCInstance = null;

/**
 * Get WebRTC service instance
 * @returns {WebRTCService}
 */
export function getWebRTCService() {
    if (!webRTCInstance) {
        webRTCInstance = new WebRTCService();
    }
    return webRTCInstance;
}

/**
 * Create a new WebRTC service instance (for new calls)
 * @returns {WebRTCService}
 */
export function createWebRTCService() {
    // Close existing instance if any
    if (webRTCInstance) {
        webRTCInstance.close();
    }
    webRTCInstance = new WebRTCService();
    return webRTCInstance;
}

export default WebRTCService;
